import React, { useState } from 'react';

/**
 * Click-to-edit pill matching the loan-amount / ROI style.
 * Behaviour: shows formatted value → click → becomes text input.
 * Enter / blur commits, Escape cancels.
 */
export default function EditablePill({
  value,
  onChange,
  min = -Infinity,
  max = Infinity,
  format,
  parse,
  suffix,
  className = '',
  width = 'w-32',
  tone = 'blue', // 'blue' | 'amber' | 'green' | 'pink'
  size = 'md',   // 'sm' | 'md'
  ...rest
}) {
  const [editing, setEditing] = useState(false);
  const [buffer, setBuffer] = useState('');

  const toneCls = {
    blue: 'border-[#B7E1FA] bg-gradient-to-b from-white to-[#EAF5FE] hover:from-[#EAF5FE] hover:to-white text-[#0176D3] dark:from-[#0F1B36] dark:to-[#1E3A5F] dark:text-sky-300 dark:border-[#1E4A72]',
    amber: 'border-[#FCD34D] bg-gradient-to-b from-white to-[#FEF3C7] hover:from-[#FEF3C7] hover:to-white text-[#78350F] dark:from-[#241A05] dark:to-[#3A2A0A] dark:text-amber-300 dark:border-[#6E4E12]',
    green: 'border-[#A7F3D0] bg-gradient-to-b from-white to-[#ECFDF5] hover:from-[#ECFDF5] hover:to-white text-[#065F46] dark:from-[#0B2A1D] dark:to-[#10362A] dark:text-emerald-300 dark:border-[#0F5D42]',
    pink: 'border-[#F9A8D4] bg-gradient-to-b from-white to-[#FDF2F8] hover:from-[#FDF2F8] hover:to-white text-[#9D174D] dark:from-[#2A1024] dark:to-[#3A1A32] dark:text-pink-300 dark:border-[#7A2D5D]',
    violet: 'border-[#C7D2FE] bg-gradient-to-b from-white to-[#EEF2FF] hover:from-[#EEF2FF] hover:to-white text-[#4338CA] dark:from-[#1B1240] dark:to-[#251A5A] dark:text-indigo-300 dark:border-[#4C3FA8]',
  }[tone];

  const focusRing = {
    blue: 'focus:ring-[#B7E1FA]/60 border-[#0176D3]',
    amber: 'focus:ring-[#FCD34D]/60 border-[#D97706]',
    green: 'focus:ring-[#A7F3D0]/60 border-[#059669]',
    pink: 'focus:ring-[#F9A8D4]/60 border-[#DB2777]',
    violet: 'focus:ring-[#C7D2FE]/60 border-[#6366F1]',
  }[tone];

  const sizeCls = size === 'sm'
    ? 'px-3 py-1.5 text-sm font-semibold rounded-md'
    : 'px-4 py-2 text-lg font-bold rounded-lg';

  const commit = () => {
    const parsed = parse ? parse(buffer) : Number(buffer);
    let v = isNaN(parsed) ? value : parsed;
    v = Math.min(max, Math.max(min, v));
    onChange(v);
    setEditing(false);
  };

  const enterEdit = () => {
    if (editing) return;
    setBuffer(String(value));
    setEditing(true);
  };

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      {editing ? (
        <input
          autoFocus
          value={buffer}
          onChange={(e) => setBuffer(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditing(false);
          }}
          className={`${width} ${sizeCls} border-2 bg-white dark:bg-[#0F1B36] text-[#032D60] dark:text-white text-right tabular-nums outline-none focus:ring-4 cursor-text ${focusRing}`}
          {...rest}
        />
      ) : (
        <button
          type="button"
          onClick={enterEdit}
          onFocus={enterEdit}
          className={`btn-3d ${width} ${sizeCls} border tabular-nums text-right transition-all cursor-text ${toneCls}`}
          title="Click or Tab to edit"
        >
          {format ? format(value) : value}
        </button>
      )}
      {suffix && <span className="text-sm font-semibold text-slate-500 dark:text-slate-400">{suffix}</span>}
    </span>
  );
}
